/**
 * The slot model: what a captain wears, whether they wear it at all, and in what order it
 * is painted.
 *
 * ---------------------------------------------------------------------------
 * Ported from `learnpixelart/pixelart` — https://github.com/learnpixelart/pixelart,
 * gem `pixelart-characters` (Ruby), originally `rubycoco/pixel`. That project carries a
 * CC0 1.0 `LICENSE.md` and states: "The scripts are dedicated to the public domain. Use
 * it as you please with no restrictions whatsoever."
 *
 * The MODEL is ported. None of that project's pixels are, and none can be: it ships no
 * part art at all — the 38 PNGs in `pixelart-characters/i/` are README samples composited
 * from user-supplied sheets its own README sources to `cryptopunksnotdead/design.punks`
 * and to the CryptoPunks Polkadot edition. Public domain on a script says nothing about
 * the art it reads, and this game's brief forbids copied protected assets. Every part
 * this file selects is drawn from parameters by `parts.ts`.
 * ---------------------------------------------------------------------------
 *
 * Three ideas are taken, and each of them answers a defect measured on this generator:
 *
 *  1. **Required vs optional, per slot.** Before this file, every layer fired on every
 *     captain. `hair` and `headwear` and `facialHair` faked absence by spending index 0
 *     of their own part set on "none", which couples two unrelated decisions: how often a
 *     captain is bald and how many hairstyles exist. Measured over 10 000 seeds, that
 *     coupling put a hat on 88.2 % of the roster, and a hat swallows the hairstyle
 *     underneath it — the distinctness scan's worst pairs were already two captains in
 *     the same cap. Presence is now its own gene and index 0 of every set is a real part.
 *
 *  2. **Gendered part tagging, `m`/`f`/`u`.** `seed.ts` has derived a pronoun set since
 *     the first version and nothing downstream ever read it for appearance except the
 *     beard roll. The tag gates WHICH part; presence odds gate WHETHER. Keeping those two
 *     apart is the whole reason facial hair stays reachable for every pronoun set — it is
 *     rare on a she/her captain, never impossible.
 *
 *  3. **Explicit z-order.** The paint order used to live in the call sequence of
 *     `paintCaptain` and in a sentence in ANCHORS §5, which is two places that can
 *     disagree. `LAYERS` is one table, sorted, and a test reads it.
 *
 * `they` is not a third gender table. It reads as `u`, which here means "reaches every
 * tag", so a they/them captain draws from the whole roster of parts rather than from a
 * leftover bucket. `she` reaches `f` + `u`; `he` reaches `m` + `u`.
 *
 * Nothing here allocates per captain: every eligibility list is built once at module load
 * and handed out frozen.
 */

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

/** How a part reads in a 64 px portrait. `u` is "either", not "neither". */
export type PartGender = 'm' | 'f' | 'u';

/** What a captain reaches for. `u` reaches everything. */
export type Reach = PartGender;

/** Does a part tagged `tag` appear in the pool of somebody reaching for `want`? */
export function allows(tag: PartGender, want: Reach): boolean {
  return want === 'u' || tag === 'u' || tag === want;
}

/** The reach a pronoun id carries. Presentation only; it gates art, never dialogue. */
export function reachOf(pronoun: 'she' | 'he' | 'they'): Reach {
  return pronoun === 'she' ? 'f' : pronoun === 'he' ? 'm' : 'u';
}

/** Eligible indices per reach, built once. `Readonly` so a caller cannot mutate the pool. */
export type Pools = Readonly<Record<Reach, readonly number[]>>;

function pools(tags: readonly PartGender[]): Pools {
  const build = (want: Reach): readonly number[] =>
    Object.freeze(tags.map((t, i) => (allows(t, want) ? i : -1)).filter((i) => i >= 0));
  return Object.freeze({ m: build('m'), f: build('f'), u: build('u') });
}

// ---------------------------------------------------------------------------
// Layers — the z-order, and which of them are optional
// ---------------------------------------------------------------------------

export type LayerId =
  | 'collar'
  | 'skull'
  | 'hair'
  | 'brow'
  | 'eyes'
  | 'nose'
  | 'facialHair'
  | 'mouth'
  | 'marks'
  | 'headwear'
  | 'rank';

export interface Layer {
  id: LayerId;
  /** Paint order, back to front. Dense, 0-based, and the array is sorted by it. */
  z: number;
  /**
   * `false` only where absence is a thing you would notice about a person. A captain with
   * no eyes is a bug; a captain with no hat is a captain.
   */
  required: boolean;
}

/**
 * Back to front, exactly as ANCHORS §5 declares it. Two of these orderings are load-
 * bearing and both were bought with a defect:
 *
 *  - `mouth` after `facialHair`, because a full beard filled as one value and 85 of 2 048
 *    captains shipped with no mouth at all.
 *  - `headwear` after `marks`, because a scar under a cap brim is a scar nobody sees.
 */
export const LAYERS: readonly Layer[] = Object.freeze([
  { id: 'collar', z: 0, required: true },
  { id: 'skull', z: 1, required: true },
  { id: 'hair', z: 2, required: false },
  { id: 'brow', z: 3, required: true },
  { id: 'eyes', z: 4, required: true },
  { id: 'nose', z: 5, required: true },
  { id: 'facialHair', z: 6, required: false },
  { id: 'mouth', z: 7, required: true },
  // Required on purpose, and it is the one place this model departs from the Ruby's
  // `required: false` default for decoration. `seed.ts` §"A distinguishing mark on every
  // captain, always" is a product decision with its own gate: the face has to carry one
  // thing a player would describe out loud. `markVar` supplies the variety instead.
  { id: 'marks', z: 8, required: true },
  { id: 'headwear', z: 9, required: false },
  { id: 'rank', z: 10, required: true },
] as const);

/** The optional layers, in `LAYERS` order. Bit i of a presence mask is `OPTIONAL[i]`. */
export const OPTIONAL: readonly LayerId[] = Object.freeze(
  LAYERS.filter((l) => !l.required).map((l) => l.id)
);

/** Presence is one integer. Three optional layers → eight states. */
export const PRESENCE_STATES = 1 << OPTIONAL.length;

const BIT: Readonly<Record<string, number>> = Object.freeze(
  Object.fromEntries(OPTIONAL.map((id, i) => [id, 1 << i]))
);

/** Bit for one optional layer. Throws at author time, not at run time, on a typo. */
export function presenceBit(id: LayerId): number {
  const b = BIT[id];
  return b === undefined ? 0 : b;
}

/** Is this layer painted on this captain? Required layers are always true. */
export function hasLayer(presence: number, id: LayerId): boolean {
  const b = BIT[id];
  return b === undefined ? true : (presence & b) !== 0;
}

// ---------------------------------------------------------------------------
// Presence odds
// ---------------------------------------------------------------------------

/**
 * P(hair), indexed by reach then age band.
 *
 * Recession is the one appearance trait where sex and age both carry real signal, and a
 * portrait that ignores it draws a roster where nobody is ever bald. The `f` row is not
 * 1.0 — a shaved head is a choice, and it is one of the loudest silhouettes available.
 */
const HAIR_ODDS: Readonly<Record<Reach, readonly number[]>> = Object.freeze({
  m: Object.freeze([0.9, 0.83, 0.73, 0.64]),
  f: Object.freeze([0.98, 0.97, 0.95, 0.93]),
  u: Object.freeze([0.95, 0.92, 0.88, 0.84]),
});

/**
 * P(facial hair). The pool of beards itself is untagged: every shape stays reachable for
 * every pronoun set, and only the frequency moves. That split is the point of separating
 * presence from eligibility, and it is what keeps `she` at 6 % rather than at 0 %.
 */
const BEARD_ODDS: Readonly<Record<Reach, readonly number[]>> = Object.freeze({
  m: Object.freeze([0.42, 0.58, 0.64, 0.66]),
  f: Object.freeze([0.05, 0.06, 0.08, 0.1]),
  u: Object.freeze([0.2, 0.26, 0.3, 0.32]),
});

/**
 * P(headwear), by temperament.
 *
 * This is the number that moved furthest. It used to be 88.2 %, an artefact of folding
 * "none" into a ten-entry part set whose other nine entries all had temperament affinity.
 * A hat takes the hairstyle's silhouette and gives back its own, so at 88 % the generator
 * was spending twenty-four hair outlines to show nine hat outlines. Around 55 % lets the
 * hair be seen on nearly half the roster and still leaves the hat a real signal.
 */
const HEADWEAR_ODDS: Readonly<Record<string, number>> = Object.freeze({
  drywit: 0.5,
  liturgist: 0.62,
  worrier: 0.48,
  veteran: 0.68,
  greenhorn: 0.58,
  clinician: 0.44,
  firebrand: 0.6,
});

/**
 * How much of the gap to certainty a bald captain closes by putting something on.
 *
 * The one conditional in this table, and it is here because a measurement asked for it.
 * A captain with neither hair nor headwear has nothing above row 33 but the skull, and
 * the skull is a finely quantised outline space: two adjacent `faceShape` values differ
 * by a pixel or two, far inside the 40-pixel head-twin threshold. At 5.2 % of the roster
 * that cohort alone doubled the head-twin rate over 4.5 million pairs, from 0.32 % to
 * 0.66 %. Widening `CRANIUM` from five archetypes to nine took most of it back; this
 * takes the cohort itself from 5.2 % to about 2 %.
 *
 * It is also just true of people. Somebody who has lost their hair on a cold ship covers
 * up more often than somebody who has not.
 */
const BALD_COVERS = 0.62;

/**
 * Odds an optional layer is present on this captain. Required layers return 1.
 *
 * `decided` is the presence mask built so far. `seed.ts` walks `LAYERS` in z-order, so a
 * layer may condition on anything painted behind it and on nothing painted in front —
 * which is the same discipline the paint loop obeys, for the same reason.
 */
export function presenceOdds(
  id: LayerId,
  reach: Reach,
  ageBand: number,
  temperament: string,
  decided: number = PRESENCE_STATES - 1
): number {
  const band = Math.max(0, Math.min(3, ageBand | 0));
  switch (id) {
    case 'hair':
      return HAIR_ODDS[reach][band];
    case 'facialHair':
      return BEARD_ODDS[reach][band];
    case 'headwear': {
      const base = HEADWEAR_ODDS[temperament] ?? 0.55;
      return hasLayer(decided, 'hair') ? base : base + (1 - base) * BALD_COVERS;
    }
    default:
      return 1;
  }
}

// ---------------------------------------------------------------------------
// Part tags
// ---------------------------------------------------------------------------

/**
 * The twenty-four hairstyles of `parts.ts`, tagged.
 *
 * Fifteen are `u`, which is deliberate: tagging every style would halve the pool a
 * captain draws from, and hair carries more head-outline distinctness than anything else
 * on the canvas. Only the strong reads are pinned — male-pattern recession one way, the
 * long masses that fall past the jaw the other.
 */
export const HAIR_TAGS: readonly PartGender[] = Object.freeze([
  'u', // 0  buzz
  'u', // 1  close crop
  'm', // 2  receded crop
  'u', // 3  straight fringe
  'u', // 4  widow's peak
  'f', // 5  heavy bob
  'u', // 6  spiked
  'm', // 7  swept back, tall
  'm', // 8  receded, long sides
  'u', // 9  tight peak
  'u', // 10 full, peaked
  'f', // 11 curtain
  'u', // 12 tall spikes
  'u', // 13 swept, low
  'u', // 14 pad
  'f', // 15 wide fringe
  'u', // 16 tall and full
  'u', // 17 short fringe
  'u', // 18 afro
  'u', // 19 big afro
  'u', // 20 bound topknot
  'f', // 21 long, past the jaw
  'f', // 22 long curtain
  'm', // 23 shaved with a queue
] as const);

/**
 * The nine brows: `weight` 1…3 crossed with `tilt` −1…1, in `parts.ts` order
 * (`weight = 1 + n % 3`, `tilt = ⌊n/3⌋ − 1`).
 *
 * Brow mass is the second cue after hair that a portrait painter uses for this, and at
 * 34 px of face it is three rows of ink against one. The heavy weight reads `m`, the
 * light weight reads `f`, and the middle weight is left open, so each reach still gets
 * six of the nine and every tilt.
 */
export const BROW_TAGS: readonly PartGender[] = Object.freeze([
  'f', // 0 light, down-tilted
  'u', // 1 medium, down-tilted
  'm', // 2 heavy, down-tilted
  'f', // 3 light, level
  'u', // 4 medium, level
  'm', // 5 heavy, level
  'f', // 6 light, up-tilted
  'u', // 7 medium, up-tilted
  'm', // 8 heavy, up-tilted
] as const);

/**
 * Headwear and uniform are untagged, and that is a finding rather than an omission.
 *
 * Every shape in both tables is issue kit on a warship — a watch cap, a cowl, a rig
 * harness, an armoured yoke. Tagging them would encode a costume convention the setting
 * does not have, and it would cost every captain a third of the pool for nothing an eye
 * can read at 64 px. Facial hair is untagged for the reason given on `BEARD_ODDS`.
 */
export const HAIR_POOLS: Pools = pools(HAIR_TAGS);
export const BROW_POOLS: Pools = pools(BROW_TAGS);

/** Every tagged table, for the gates that check them all the same way. */
export const TAGGED: Readonly<Record<string, { tags: readonly PartGender[]; pools: Pools }>> =
  Object.freeze({
    hair: { tags: HAIR_TAGS, pools: HAIR_POOLS },
    brows: { tags: BROW_TAGS, pools: BROW_POOLS },
  });
